/**
 * Web mirror of packages/domain/src/place.ts.
 * CRA cannot transpile workspace TypeScript packages directly.
 * Keep in sync with @bilt/domain place helpers (mobile uses the package).
 */

export interface PlaceCoordinates {
  place_id?: string | null;
  formatted_address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  city?: string | null;
  state?: string | null;
}

export interface PlaceDisplayInput extends PlaceCoordinates {
  /** Events: optional venue label stored in `location`. */
  location_label?: string | null;
}

export function hasValidatedPlace(input: PlaceDisplayInput): boolean {
  return Boolean(
    input.place_id &&
      input.latitude != null &&
      input.longitude != null &&
      Number.isFinite(input.latitude) &&
      Number.isFinite(input.longitude),
  );
}

export function formatPlaceHeadline(input: PlaceDisplayInput): string | null {
  const label = input.location_label?.trim();
  const address = input.formatted_address?.trim();
  if (label) return label;
  if (address) return address;
  const cityState = [input.city, input.state].filter(Boolean).join(', ');
  return cityState || null;
}

export function formatPlaceSubline(input: PlaceDisplayInput): string | null {
  const label = input.location_label?.trim();
  const address = input.formatted_address?.trim();
  if (label && address) return address;
  if (!address) {
    return [input.city, input.state].filter(Boolean).join(', ') || null;
  }
  const cityState = [input.city, input.state].filter(Boolean).join(', ');
  if (cityState && !address.includes(cityState)) return cityState;
  return null;
}

export function googleMapsDirectionsUrl(input: PlaceDisplayInput): string | null {
  if (input.place_id) {
    return `https://www.google.com/maps/dir/?api=1&destination_place_id=${encodeURIComponent(input.place_id)}`;
  }
  if (input.latitude != null && input.longitude != null) {
    return `https://www.google.com/maps/dir/?api=1&destination=${input.latitude},${input.longitude}`;
  }
  const dest =
    input.formatted_address?.trim() || [input.city, input.state].filter(Boolean).join(', ');
  if (dest) {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;
  }
  return null;
}

export function googleMapsViewUrl(input: PlaceDisplayInput): string | null {
  if (input.place_id) {
    return `https://www.google.com/maps/search/?api=1&query_place_id=${encodeURIComponent(input.place_id)}`;
  }
  if (input.latitude != null && input.longitude != null) {
    return `https://www.google.com/maps/search/?api=1&query=${input.latitude},${input.longitude}`;
  }
  const query =
    input.formatted_address?.trim() || [input.city, input.state].filter(Boolean).join(', ');
  if (query) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  }
  return null;
}

export function buildStaticMapUrl(opts: {
  apiKey: string;
  latitude: number;
  longitude: number;
  width?: number;
  height?: number;
}): string {
  const { apiKey, latitude, longitude, width = 640, height = 280 } = opts;
  const center = `${latitude},${longitude}`;
  const markers = `color:0xea580c|${center}`;
  const params = new URLSearchParams({
    center,
    zoom: '15',
    size: `${width}x${height}`,
    scale: '2',
    markers,
    key: apiKey,
  });
  return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
}
